"use client";

import React from "react";
import { Diamond, Check, X } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const DiamondRating = ({
  rating = 0
}: {
  rating?: number;
}) => {
  const clampedRating = Math.min(Math.max(rating, 0), 5);
  return <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((index, i) => <Diamond key={i} className={`size-4 ${index <= clampedRating ? "fill-primary text-primary" : "fill-muted text-muted-foreground/30"}`} />)}
    </div>;
};
function GearCarousel() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex w-full gap-6 overflow-hidden">
        {[1, 2, 3].map(i => <div key={i} className="h-[500px] w-full shrink-0 animate-pulse rounded-xl bg-muted/10 md:w-1/3" />)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-6 text-center text-destructive-foreground">
        <p>Unable to load gear reviews. Please try again later.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">
        <p>No gear reviews found.</p>
      </div>;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4">
        {posts.map((post, i) => {
        const rating = post.customFields?.rating ? Number(post.customFields.rating) : 4;
        const pros = post.customFields?.pros || [];
        const cons = post.customFields?.cons || [];
        const hasProsCons = Array.isArray(pros) && pros.length > 0;
        return <CarouselItem key={post.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
              <Card className="h-full overflow-hidden border-border/40 bg-card transition-colors hover:border-primary/30">
                {}
                <div className="relative flex aspect-[4/3] items-center justify-center bg-neutral-100 p-8 dark:bg-neutral-900/50">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="max-h-full w-auto object-contain drop-shadow-md transition-transform duration-500 hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-neutral-200 text-neutral-400 dark:bg-neutral-800">
                      <span className="text-sm font-medium">No Image</span>
                    </div>}
                  <div className="absolute top-4 right-4 rounded-full bg-background/90 px-3 py-1 text-xs font-bold text-foreground shadow-sm backdrop-blur-sm">
                    Tested
                  </div>
                </div>

                <CardHeader className="pb-2">
                  <div className="mb-2 flex items-center justify-between">
                    <DiamondRating rating={rating} />
                    <span className="text-xs font-medium text-muted-foreground">
                      {rating}/5
                    </span>
                  </div>
                  <CardTitle className="line-clamp-1 text-xl">
                    {post.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-grow space-y-4">
                  {}
                  {hasProsCons ? <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/30 p-3 text-xs">
                      <div>
                        <span className="mb-2 block font-bold text-emerald-500">Pros</span>
                        <ul className="space-y-1 text-muted-foreground">
                          {pros.slice(0, 2).map((pro: string, idx: number) => <li key={idx} className="flex items-start gap-1.5">
                              <Check className="mt-0.5 size-3 shrink-0 text-emerald-500" />
                              <span className="line-clamp-1">{pro}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div>
                        <span className="mb-2 block font-bold text-rose-500">Cons</span>
                        <ul className="space-y-1 text-muted-foreground">
                          {cons.slice(0, 2).map((con: string, idx: number) => <li key={idx} className="flex items-start gap-1.5">
                              <X className="mt-0.5 size-3 shrink-0 text-rose-500" />
                              <span className="line-clamp-1">{con}</span>
                            </li>)}
                        </ul>
                      </div>
                    </div> : <CardDescription className="line-clamp-3 min-h-[5rem]">
                      {post.excerpt ? <div dangerouslySetInnerHTML={{
                  __html: post.excerpt
                }} /> : "Read the full review to see our detailed breakdown of this gear's performance in the field."}
                    </CardDescription>}
                </CardContent>

                <CardFooter className="pt-2">
                  <Link to={post.link || "#"} className="w-full">
                    <Button className="w-full font-bold uppercase tracking-wide" size="lg">
                      Read Review
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </CarouselItem>;
      })}
      </CarouselContent>
      <div className="mt-8 flex justify-end gap-2 pr-4">
        <CarouselPrevious className="static translate-y-0 hover:bg-primary hover:text-primary-foreground" />
        <CarouselNext className="static translate-y-0 hover:bg-primary hover:text-primary-foreground" />
      </div>
    </Carousel>;
}
export default function OutBackGearReview() {
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 8,
    orderby: "date",
    order: "desc"
  });
  return <section id="out-back-gear-review" className="dark bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-4">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl font-bold tracking-tight text-primary md:text-6xl">
              Out & Back
            </h2>
            <p className="mt-4 text-lg text-muted-foreground md:text-xl">
              Field-tested gear reviews for the modern explorer. We put equipment through the wringer so you don't have to.
            </p>
          </div>
          <Link to="/OutAndBackPage">
            <Button variant="outline" className="hidden md:flex">
              View All Reviews
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query}>
          <GearCarousel />
        </PostsProvider>

        <div className="mt-8 flex justify-center md:hidden">
          <Link to="/OutAndBackPage">
            <Button variant="outline" className="w-full">
              View All Reviews
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}